/**
 * Where an entity went when a merge folded it into another one.
 *
 * A merge deletes the merged node, and whoever still holds its id — a saved
 * investigation's snapshot, a bookmark, a stale list row — reads a node that is
 * gone. The reads that look an entity up by id answer `410 Gone` for those ids
 * instead of a bare 404, naming the survivor from the merge audit: the graph
 * node route, and the identity and face-matching reads (ADR-0053). Mirrors
 * `routers/v1/_merged_entity.py`.
 */
import { ApiError } from '@/services/api/client'

export interface MergedEntityRedirect {
  canonicalNodeId: string
  canonicalNodeName: string | null
  mergeReason: string | null
  /** ISO-8601 UTC */
  mergedAt: string | null
}

/** The redirect a failed read carries, or null when it failed for any other reason. */
export function parseMergedRedirect(error: unknown): MergedEntityRedirect | null {
  if (!(error instanceof ApiError) || error.status !== 410) return null
  const detail = (error.data as { detail?: unknown } | undefined)?.detail
  if (!detail || typeof detail !== 'object') return null
  const body = detail as Record<string, unknown>
  if (body.reason !== 'entity_merged' || typeof body.canonical_node_id !== 'string') return null
  const asString = (value: unknown) => (typeof value === 'string' ? value : null)
  return {
    canonicalNodeId: body.canonical_node_id,
    canonicalNodeName: asString(body.canonical_node_name),
    mergeReason: asString(body.merge_reason),
    mergedAt: asString(body.merged_at),
  }
}

/** A missing (404) or merged-away (410) entity: an answer that will not change. */
export function isGoneForGood(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 410)
}

/**
 * The `retry` of a query that reads an entity by id: three retries as usual,
 * and none once the entity is gone for good — retrying only delays what the
 * view is about to show, or where it moves.
 */
export function retryUnlessGone(failureCount: number, error: unknown): boolean {
  return !isGoneForGood(error) && failureCount < 3
}
